import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Tab, TabList, TabPanel, TabPanels, Tabs, Tag, TextInput } from '@carbon/react';
import { Search } from '@carbon/icons-react';
import { vnDayKey } from '@ruby-cms/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PageHeader, ErrorState, BlockSkeleton, EmptyState } from '../components/states';
import { Money, When } from '../components/format';
import { FilterBar, useUrlFilters } from '../components/Filters';
import { LineChart, Num, Panel, Rate, scopeFields, shiftDay, useOptions, useReportFilters } from '../components/Analytics';

// Screen 6 of docs/cms-golive/kpi-catalogue.md: whale concentration (top 1% / 10%, Gini, Pareto),
// the churn-risk list and the player 360 profile (P1-D17).

type Share = { players: number; value: number; share: number | null };
type Whales = {
  players: number; totalGgr: number; totalTurnover: number;
  ggr: { top1: Share; top10: Share }; turnover: { top1: Share; top10: Share; gini: number | null };
  pareto: { ggr: { pct: number; share: number | null }[]; turnover: { pct: number; share: number | null }[] };
  top: { userId: number; nick: string | null; ggr: number; turnover: number; share: number | null }[];
};

function PlayerLink({ id, nick }: { id: number; nick: string | null }) {
  const { can } = useAuth();
  return can('users.view') ? <Link className="cell-link" to={`/stats/player/${id}`}>{nick ?? '#' + id}</Link> : <>{nick ?? '#' + id}</>;
}

function WhalesTab() {
  const o = useOptions();
  const f = useReportFilters(30);
  const q = useQuery({ queryKey: ['analytics', 'whales', f.query], queryFn: () => api.get<Whales>('/api/admin/analytics/whales', f.query) });
  const d = q.data;
  return (
    <>
      <FilterBar id="wh" fields={scopeFields(o.data, { game: true })} filters={f} exportPath="/api/admin/analytics/whales" />
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !d?.players ? (
        <EmptyState title="Không có người chơi nào cược trong khoảng này" />
      ) : (
        <>
          <div className="kpis">
            <div className="kpi kpi--lead"><span className="kpi__label">Top 1% người chơi mang lại</span><span className="kpi__value"><Rate v={d.ggr.top1.share} /></span><span className="kpi__hint">GGR của {d.ggr.top1.players} người, <Money v={d.ggr.top1.value} unit /></span></div>
            <div className="kpi"><span className="kpi__label">Top 10% mang lại</span><span className="kpi__value"><Rate v={d.ggr.top10.share} /></span><span className="kpi__hint">GGR của {d.ggr.top10.players} người, <Money v={d.ggr.top10.value} unit /></span></div>
            <div className="kpi"><span className="kpi__label">Doanh số top 1% / 10%</span><span className="kpi__value"><Rate v={d.turnover.top1.share} /> / <Rate v={d.turnover.top10.share} /></span><span className="kpi__hint">tổng doanh số <Money v={d.totalTurnover} unit /></span></div>
            <div className="kpi"><span className="kpi__label">Hệ số Gini doanh số</span><span className="kpi__value"><Num v={d.turnover.gini} digits={2} /></span><span className="kpi__hint">0 = mọi người cược như nhau, 1 = một người cược hết; {d.players} người</span></div>
          </div>
          <Panel title="Đường Pareto (phần trăm người chơi xếp từ cao xuống, phần GGR và doanh số cộng dồn)">
            <LineChart title="Pareto" labels={d.pareto.ggr.map((p) => p.pct + '%')} format={(v) => Math.round(v * 100) + '%'}
              series={[{ label: 'GGR', points: d.pareto.ggr.map((p) => p.share) }, { label: 'Doanh số', points: d.pareto.turnover.map((p) => p.share) }]} />
          </Panel>
          <Panel title="Top 10% theo GGR">
            <table className="plain">
              <thead><tr><th className="r">#</th><th>Người chơi</th><th className="r">GGR</th><th className="r">Doanh số</th><th className="r">Tỷ trọng GGR</th></tr></thead>
              <tbody>{d.top.map((r, i) => (
                <tr key={r.userId}><td className="r num muted">#{i + 1}</td><td className="is-title"><PlayerLink id={r.userId} nick={r.nick} /></td>
                  <td className="r"><Money v={r.ggr} signed /></td><td className="r"><Money v={r.turnover} /></td><td className="r"><Rate v={r.share} /></td></tr>
              ))}</tbody>
            </table>
          </Panel>
        </>
      )}
      <p className="muted note">GGR = cược - trả thưởng - hoàn; doanh số = cược - hoàn. Người thắng nhà cái có GGR âm và làm giảm tổng.</p>
    </>
  );
}

type Risk = { factor: number; minDays: number; items: { userId: number; nick: string | null; lastPlay: string; daysSince: number; usualGap: number; playDays: number; deposits: number; ggr90: number }[] };

function ChurnTab() {
  const o = useOptions();
  const f = useUrlFilters(['agent', 'source', 'game', 'category']);
  const q = useQuery({ queryKey: ['analytics', 'churn-risk', f.query], queryFn: () => api.get<Risk>('/api/admin/analytics/churn-risk', f.query) });
  const fields = scopeFields(o.data, { dates: false, game: true }).filter((x) => x.key !== 'depositors');
  return (
    <>
      <FilterBar id="cr" fields={fields} filters={f} exportPath="/api/admin/analytics/churn-risk" />
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !q.data?.items.length ? (
        <EmptyState title="Không có người nạp nào đang có nguy cơ rời bỏ" />
      ) : (
        <table className="plain">
          <thead><tr><th>Người chơi</th><th>Cược lần cuối</th><th className="r">Đã nghỉ</th><th className="r">Khoảng cách thường lệ</th><th className="r">Số ngày chơi (90 ngày)</th><th className="r">Tổng nạp</th><th className="r">GGR 90 ngày</th></tr></thead>
          <tbody>{q.data.items.map((r) => (
            <tr key={r.userId}>
              <td className="is-title"><PlayerLink id={r.userId} nick={r.nick} /></td><td className="num">{r.lastPlay}</td>
              <td className="r"><Num v={r.daysSince} /> ngày</td><td className="r"><Num v={r.usualGap} digits={1} /> ngày</td><td className="r"><Num v={r.playDays} /></td>
              <td className="r"><Money v={r.deposits} /></td><td className="r"><Money v={r.ggr90} signed /></td>
            </tr>
          ))}</tbody>
        </table>
      )}
      {q.data ? <p className="muted note">Người đã nạp, có ít nhất {q.data.minDays} ngày cược trong 90 ngày, đã nghỉ lâu hơn {q.data.factor} lần khoảng cách trung vị giữa các ngày chơi của chính họ và chưa quá 30 ngày (quá 30 ngày là đã rời bỏ). Hai hệ số nằm trong Cài đặt.</p> : null}
    </>
  );
}

type P360 = {
  user: { id: number; nick: string; userName: string; createdAt: string; balance: number; isTest: boolean; agent: string | null; ftdAt: string | null; ftdAmount: string | null };
  lifetime: { deposits: number; depositCount: number; withdrawals: number; withdrawalCount: number; net: number; ggr: number; bonus: number };
  range: { from: string; to: string; ggr: number; bonus: number; commission: number; ngr: number };
  games: { game: string; bet: number; win: number; refund: number; ggr: number; bets: number; days: number }[];
};

export function Player360({ id }: { id: number }) {
  const today = vnDayKey(new Date());
  const [range] = useState({ from: shiftDay(today, -29), to: today });
  const q = useQuery({ queryKey: ['analytics', 'player', id, range], queryFn: () => api.get<P360>(`/api/admin/analytics/player/${id}`, range) });
  if (q.isLoading) return <BlockSkeleton lines={10} />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data!;
  return (
    <>
      <div className="row">
        <h2>{d.user.nick}</h2>{d.user.isTest ? <Tag type="gray" size="sm">Tài khoản thử</Tag> : null}
        <Link className="cell-link" to={`/users/${d.user.id}`}>Mở trang người chơi</Link>
      </div>
      <dl className="facts section">
        <dt>Mã, tên đăng nhập</dt><dd className="num">#{d.user.id} {d.user.userName}</dd>
        <dt>Đăng ký</dt><dd><When v={d.user.createdAt} /> {d.user.agent ? <>qua đại lý <strong>{d.user.agent}</strong></> : null}</dd>
        <dt>Nạp lần đầu</dt><dd>{d.user.ftdAt ? <><When v={d.user.ftdAt} />, <Money v={d.user.ftdAmount} unit /></> : <span className="muted">chưa nạp</span>}</dd>
        <dt>Số dư</dt><dd><Money v={d.user.balance} unit /></dd>
      </dl>
      <div className="kpis section">
        <div className="kpi"><span className="kpi__label">Tổng nạp</span><span className="kpi__value"><Money v={d.lifetime.deposits} unit /></span><span className="kpi__hint">{d.lifetime.depositCount} lần</span></div>
        <div className="kpi"><span className="kpi__label">Tổng rút đã trả</span><span className="kpi__value"><Money v={d.lifetime.withdrawals} unit /></span><span className="kpi__hint">{d.lifetime.withdrawalCount} lần</span></div>
        <div className="kpi"><span className="kpi__label">Nạp ròng</span><span className="kpi__value"><Money v={d.lifetime.net} signed unit /></span><span className="kpi__hint">nạp - rút</span></div>
        <div className="kpi"><span className="kpi__label">GGR trọn đời</span><span className="kpi__value"><Money v={d.lifetime.ggr} signed unit /></span><span className="kpi__hint">thưởng đã nhận <Money v={d.lifetime.bonus} /></span></div>
        <div className="kpi"><span className="kpi__label">NGR 30 ngày</span><span className="kpi__value"><Money v={d.range.ngr} signed unit /></span><span className="kpi__hint">GGR <Money v={d.range.ggr} signed />, thưởng <Money v={d.range.bonus} />, hoa hồng <Money v={d.range.commission} /></span></div>
      </div>
      <div className="split">
        <Panel title="Game đã chơi">
          {d.games.length ? (
            <table className="plain">
              <thead><tr><th>Game</th><th className="r">Cược</th><th className="r">GGR</th><th className="r">Lần cược</th><th className="r">Ngày</th></tr></thead>
              <tbody>{d.games.map((g) => <tr key={g.game}><td className="is-title">{g.game}</td><td className="r"><Money v={g.bet} /></td><td className="r"><Money v={g.ggr} signed /></td><td className="r"><Num v={g.bets} /></td><td className="r"><Num v={g.days} /></td></tr>)}</tbody>
            </table>
          ) : <p className="muted">Chưa cược game nào.</p>}
        </Panel>
      </div>
    </>
  );
}

function LookupTab() {
  const nav = useNavigate();
  const [id, setId] = useState('');
  const go = (e: FormEvent) => { e.preventDefault(); if (/^\d+$/.test(id)) nav(`/stats/player/${id}`); };
  return (
    <form className="toolbar-filters" onSubmit={go}>
      <TextInput id="p360-id" labelText="Mã người chơi" inputMode="numeric" value={id} onChange={(e) => setId(e.target.value.replace(/\D/g, ''))} />
      <Button type="submit" kind="tertiary" size="md" renderIcon={Search} disabled={!id}>Xem hồ sơ</Button>
    </form>
  );
}

export function StatsPlayerPage() {
  const { id } = useParams();
  return (
    <div className="page">
      <PageHeader title="Hồ sơ người chơi 360" lead="Nạp, rút, GGR, thưởng, game, phiên, tài khoản liên quan và thao tác của quản trị." actions={<Link className="cell-link" to="/stats/segments">Về phân khúc</Link>} />
      <Player360 id={Number(id)} />
    </div>
  );
}

export function StatsSegmentsPage() {
  const [tab, setTab] = useState(0);
  const { can } = useAuth();
  return (
    <div className="page">
      <PageHeader title="Phân khúc người chơi" lead="Cá voi, nguy cơ rời bỏ và hồ sơ 360 của một người chơi. Chỉ tính người chơi thật." />
      <Tabs selectedIndex={tab} onChange={({ selectedIndex }) => setTab(selectedIndex)}>
        <TabList aria-label="Phân khúc">
          <Tab>Cá voi</Tab>
          <Tab>Nguy cơ rời bỏ</Tab>
          <Tab disabled={!can('users.view')}>Hồ sơ 360</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>{tab === 0 ? <WhalesTab /> : null}</TabPanel>
          <TabPanel>{tab === 1 ? <ChurnTab /> : null}</TabPanel>
          <TabPanel>{tab === 2 ? <LookupTab /> : null}</TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
}

